/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	Pressable,
	View,
	Text,
	Icon,
	BoxSpinnerView,
	BoxEmptyView,
} from '@components/atoms'
import { AccessControlMenuList } from '@components/systems/accessControl/AccessControlMenuList'
import { Collapse } from '@mui/material'

import {
	useAppSelector,
	ApiGetDataStatus,
	MenuTreeNodeType,
	MenuType,
} from '@redux/index'
import { MenuTreeNode, AccessControlType, AccessGroupType } from '@schemas'
import { grey } from '@mui/material/colors'

interface PropsType {
	status: ApiGetDataStatus
	accessControlList: AccessControlType[]
	isCollapsible?: boolean
	isSelectedGroup?: boolean
	detailItemStyle?: React.CSSProperties
	isShownDeleteButton?: boolean
	onDeleteClick?: (accessControlId: string) => void
}

export const AccessControlView = (props: PropsType) => {
	const { status, accessControlList, isSelectedGroup } = props

	// redux state
	const selectedTab: string = useAppSelector(state => state.accessControl.selectedTab)
	const mainMenuTreeList: MenuTreeNodeType[] = useAppSelector(
		state => state.admin.menuTreeList,
	)
	const adminMenu: MenuType[] = useAppSelector(state => state.admin.menu)
	const selectedGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)

	// variables
	const isDisabled: boolean = selectedGroup.id === ''
	const adminMenuTreeList: MenuTreeNodeType[] = React.useMemo(() => {
		const res = adminMenu.map((anAdminMenu, index) => {
			return new MenuTreeNode({
				...anAdminMenu,
				text: anAdminMenu.name,
				droppable: false,
				isActivate: true,
				order: index,
				parent: 'root',
				i18n: [],
			})
		})

		return res
	}, [adminMenu])

	const defaultMenuSectionList: ('main' | 'admin')[] = ['main', 'admin']

	// state
	const [collapsedList, setCollapsedList] =
		React.useState<string[]>(defaultMenuSectionList)

	const onMenuSectionClick = (sectionName: 'main' | 'admin') => {
		const newCollapsedList = [...collapsedList]
		const index: number = newCollapsedList.indexOf(sectionName)
		if (index > -1) {
			newCollapsedList.splice(index, 1)
		} else {
			newCollapsedList.push(sectionName)
		}

		setCollapsedList(newCollapsedList)
	}

	return (
		<View css={[style]}>
			<View className="accessControlInnerView">
				{status === 'init' ||
					(status === 'loading' && (
						<View className="spinnerView">
							<BoxSpinnerView />{' '}
						</View>
					))}

				{selectedTab === 'user' && isSelectedGroup !== undefined && !isSelectedGroup ? (
					<BoxEmptyView isGrey={true} title={'그룹을 선택해주세요.'} />
				) : (
					<>
						{defaultMenuSectionList.map(aSection => {
							const isCollapseOpend: boolean = collapsedList.includes(aSection)
							return (
								<View key={aSection} className="menuListOuter">
									<View className="menuSectionView">
										<Pressable
											className="menuSectionButton"
											onClick={() => onMenuSectionClick(aSection)}
										>
											<Text className="menuSectionTitle">
												{aSection === 'main' ? '메인 메뉴' : '어드민 메뉴'}
											</Text>

											<View className="menuSectionChevronView">
												<Icon
													className="menuSectionChevronIcon"
													name={isCollapseOpend ? 'chevron-down' : 'chevron-right'}
												/>
											</View>
										</Pressable>
									</View>

									<Collapse in={collapsedList.includes(aSection)} timeout="auto">
										<AccessControlMenuList
											accessControlList={accessControlList}
											menuTreeList={
												aSection === 'main' ? mainMenuTreeList : adminMenuTreeList
											}
											isDisabled={isDisabled}
										/>
									</Collapse>
								</View>
							)
						})}
					</>
				)}
			</View>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',
	overflow: 'auto',

	'.accessControlInnerView': {
		width: '100%',
		height: '100%',
		padding: '0 0.2rem',

		'.spinnerView': {
			position: 'absolute',
			zIndex: 9,
			width: '100%',
			height: '100%',
			top: 0,
			background: '#FFF',
			opacity: 0.5,
		},

		'.menuListOuter': {
			marginBottom: '0.5rem',
			background: grey[200],
			border: '1px solid',
			borderColor: grey[300],
			borderRadius: '5px',
			overflow: 'hidden',

			'.menuSectionView': {
				width: '100%',

				'.menuSectionButton': {
					position: 'relative',
					width: '100%',
					marginBottom: '0.2rem',
					background: grey[50],
					borderBottom: '1px solid',
					borderColor: grey[300],

					'.menuSectionChevronView': {
						position: 'absolute',
						display: 'flex',
						alignItems: 'center',
						top: 0,
						bottom: 0,
						right: '1rem',
						margin: 'auto',

						'.menuSectionChevronIcon': {},
					},

					'.menuSectionTitle': {
						textAlign: 'left',
					},
				},
			},
		},
	},
})
